import { captureError } from '@smg-real-estate/browser-rum';

type ConsoleWarn = typeof console.warn;

interface VueComponentInstance {
  uid?: number;
  parent?: VueComponentInstance | null;
  type?: unknown;
  vnode?: {
    type?: unknown;
  };
}

interface VueHydrationNode extends Node {
  __vueParentComponent?: VueComponentInstance | null;
}

const HYDRATION_MISMATCH_WARNING_PATTERN = /hydration[\s\S]*mismatch/i;
const RUM_ENABLED_STAGES = ['test', 'prod'];
const MAX_COMPONENT_TRACE_LENGTH = 10;

let restoreConsoleWarn: (() => void) | undefined;

export const isHydrationMismatchWarning = (message: string): boolean =>
  HYDRATION_MISMATCH_WARNING_PATTERN.test(message);

const isRumEnabled = (): boolean =>
  typeof window !== 'undefined' && RUM_ENABLED_STAGES.includes(import.meta.env.VUE_APP_STAGE);

const isNode = (value: unknown): value is Node =>
  typeof Node !== 'undefined' && value instanceof Node;

const getComponentTypeName = (type: unknown): string | undefined => {
  if (typeof type === 'string') {
    return type;
  }

  if (typeof type === 'function') {
    return type.name || undefined;
  }

  if (!type || typeof type !== 'object') {
    return undefined;
  }

  const componentType = type as { name?: string; __name?: string };
  return componentType.name ?? componentType.__name;
};

const findComponentInstance = (node: Node | undefined): VueComponentInstance | undefined => {
  let currentNode: Node | null | undefined = node;

  while (currentNode) {
    const component = (currentNode as VueHydrationNode).__vueParentComponent;
    if (component) {
      return component;
    }

    currentNode = currentNode.parentNode;
  }

  return undefined;
};

const getComponentTrace = (instance: VueComponentInstance | undefined): string[] => {
  const trace: string[] = [];
  let currentInstance = instance;

  while (currentInstance && trace.length < MAX_COMPONENT_TRACE_LENGTH) {
    const componentName = getComponentTypeName(currentInstance.type ?? currentInstance.vnode?.type);

    if (componentName) {
      trace.push(componentName);
    }

    currentInstance = currentInstance.parent ?? undefined;
  }

  return trace;
};

const getSafeWarningMessage = (args: Parameters<ConsoleWarn>): string =>
  args
    .filter((arg) => !isNode(arg))
    .map((arg) => String(arg))
    .join(' ')
    .trim();

export const reportHydrationMismatch = (args: Parameters<ConsoleWarn>): void => {
  if (!isRumEnabled()) {
    return;
  }

  const message = getSafeWarningMessage(args);
  if (!isHydrationMismatchWarning(message)) {
    return;
  }

  const hydrationNode = args.find(isNode);
  const component = findComponentInstance(hydrationNode);
  const componentTrace = getComponentTrace(component);
  const error = new Error(`Vue hydration mismatch: ${message}`);
  error.name = 'VueHydrationMismatch';

  captureError(error, {
    source: 'vue-hydration',
    hydration_message: message,
    hydration_component: componentTrace[0],
    hydration_component_uid: component?.uid,
    hydration_component_trace: componentTrace.length ? componentTrace.join(' > ') : undefined,
    url: window.location.pathname,
  });
};

export const installHydrationMismatchCapture = (): (() => void) => {
  if (restoreConsoleWarn) {
    return restoreConsoleWarn;
  }

  if (!isRumEnabled()) {
    return () => undefined;
  }

  const originalWarn = console.warn;
  console.warn = (...args: Parameters<ConsoleWarn>) => {
    reportHydrationMismatch(args);
    originalWarn.apply(console, args);
  };

  restoreConsoleWarn = () => {
    console.warn = originalWarn;
    restoreConsoleWarn = undefined;
  };

  return restoreConsoleWarn;
};
